import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const AddSalaryModal = ({ employee, salaryHeads, onDefineSalary, onClose }) => {  // Added onClose prop for modal control
  const [effectiveDate, setEffectiveDate] = useState(new Date());
  const [earnings, setEarnings] = useState([{ id: 1, salaryHead: null, amount: "" }]);
  const [deductions, setDeductions] = useState([{ id: 1, salaryHead: null, amount: "" }]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [totalDeductions, setTotalDeductions] = useState(0);
  const [grossSalary, setGrossSalary] = useState(0);
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const amountRef = useRef(null);

  const safeEmployee = {
    ...employee,
    salary_breakdown: employee?.salary_breakdown || { earnings: [], deductions: [] },
  };

  useEffect(() => {
    if (employee) {
      if (employee.salary_status === "Existing" && employee.salary_breakdown) {
        setEarnings(
          employee.salary_breakdown.earnings.map((e, index) => ({
            id: index + 1,
            salaryHead: { value: e.head_id, label: e.head },
            amount: e.amount,
          }))
        );
        setDeductions(
          employee.salary_breakdown.deductions.map((d, index) => ({
            id: index + 1,
            salaryHead: { value: d.head_id, label: d.head },
            amount: d.amount,
          }))
        );
      } else {
        resetForm();
      }
    }
  }, [employee]);

  const resetForm = () => {
    setEffectiveDate(new Date());
    setEarnings([{ id: 1, salaryHead: null, amount: "" }]);
    setDeductions([{ id: 1, salaryHead: null, amount: "" }]);
    setTotalEarnings(0);
    setTotalDeductions(0);
    setGrossSalary(0);
  };

  useEffect(() => {
    const earningsSum = earnings.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
    const deductionsSum = deductions.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
    setTotalEarnings(earningsSum);
    setTotalDeductions(deductionsSum);
    setGrossSalary(earningsSum - deductionsSum);
  }, [earnings, deductions]);

  const addEarning = () => {
    setEarnings([...earnings, { id: Date.now(), salaryHead: null, amount: "" }]);
  };

  const removeEarning = (id) => {
    setEarnings(earnings.filter((item) => item.id !== id));
  };

  const addDeduction = () => {
    setDeductions([...deductions, { id: Date.now(), salaryHead: null, amount: "" }]);
  };

  const removeDeduction = (id) => {
    setDeductions(deductions.filter((item) => item.id !== id));
  };

  const handleAmountChange = (e, index, type) => {
    let newAmount = e.target.value.replace(/[^0-9.]/g, '');  // Allow only numbers/decimals
    if (parseFloat(newAmount) < 0) newAmount = '';  // Prevent negative
    if (type === "earning") {
      const newEarnings = [...earnings];
      newEarnings[index].amount = newAmount;
      setEarnings(newEarnings);
    } else {
      const newDeductions = [...deductions];
      newDeductions[index].amount = newAmount;
      setDeductions(newDeductions);
    }
  };

  const handleAmountKeyDown = (e, index, type) => {  // Separate for Enter key
    if (e.key === "Enter" && e.target.value) {
      if (type === "earning" && index === earnings.length - 1) addEarning();
      if (type === "deduction" && index === deductions.length - 1) addDeduction();
    }
  };

  const handleHeadSelection = (selectedOption, index, type) => {
    if (type === "earning") {
      const newEarnings = [...earnings];
      newEarnings[index].salaryHead = selectedOption;
      setEarnings(newEarnings);
      // TODO: If head has formula, auto-compute amount here
      amountRef.current?.focus();
    } else {
      const newDeductions = [...deductions];
      newDeductions[index].salaryHead = selectedOption;
      setDeductions(newDeductions);
      // TODO: Auto-compute for statutory like PF
      amountRef.current?.focus();
    }
  };

  const filterOptions = (type) => {
    const selectedHeads = type === "earning"
      ? earnings.map((e) => e.salaryHead?.value)
      : deductions.map((d) => d.salaryHead?.value);

    return salaryHeads
      .filter((head) => head.category.toLowerCase() === type.toLowerCase())
      .map((head) => ({
        value: head.id,
        label: head.name,
      }))
      .filter((option) => !selectedHeads.includes(option.value));
  };

  const handleSave = async () => {
    const validEarnings = earnings.filter((item) => item.salaryHead && parseFloat(item.amount) > 0);
    const validDeductions = deductions.filter((item) => item.salaryHead && parseFloat(item.amount) >= 0);  // Deductions can be 0

    if (validEarnings.length === 0) {
      toast.error("Please add at least one valid earning with a positive amount.");
      return;
    }

    const salaryDetails = {
      employee_id: employee.id,
      gross_salary: totalEarnings,
      deductions: totalDeductions,
      net_salary: grossSalary,
      salary_breakdown: {
        earnings: validEarnings.map((item) => ({
          head_id: item.salaryHead.value,
          head: item.salaryHead.label,
          amount: parseFloat(item.amount),
        })),
        deductions: validDeductions.map((item) => ({
          head_id: item.salaryHead.value,
          head: item.salaryHead.label,
          amount: parseFloat(item.amount),
        })),
      },
      effective_date: effectiveDate.toISOString().split('T')[0],  // Format as YYYY-MM-DD
    };

    try {
      const response = await axios.post(`${apiUrl}/payroll/allocate-salary`, salaryDetails, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Salary details saved successfully!");
      if (onDefineSalary) onDefineSalary();
      resetForm();
      if (onClose) onClose();  // Call onClose to dismiss modal
    } catch (error) {
      toast.error("Failed to save salary details. Please try again.");
    }
  };

  return (
    <div id="add_salary" className="modal custom-modal fade custom-modal-width" role="dialog">
      <div
        className="modal-dialog modal-lg"
        style={{
          margin: 0,
          position: "fixed",
          right: 0,
          height: "100vh",
          width: "80%",  // Reduced from 100% for better UX
        }}
        role="document"
      >
        <div className="modal-content h-100">
          <div className="modal-header">
            <h5 className="modal-title">
              {employee?.salary_status === "Existing" ? "Revise Salary" : "Define Salary"}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} aria-label="Close">
              <span aria-hidden="true">×</span>
            </button>
          </div>
          <div className="modal-body" style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                border: "1px solid #ccc",
                borderRadius: "5px",
                padding: "10px",
                background: "#f9f9f9",
              }}
            >
              <div style={{ flex: "1 1 25%", marginBottom: "10px" }}>
                <strong>Name:</strong> {employee?.name || "N/A"}
              </div>
              <div style={{ flex: "1 1 25%", marginBottom: "10px" }}>
                <strong>Employee ID:</strong> {employee?.empcode || "N/A"}
              </div>
              <div style={{ flex: "1 1 25%", marginBottom: "10px" }}>
                <strong>Department:</strong> {employee?.department_name || "N/A"}
              </div>
              <div style={{ flex: "1 1 25%", marginBottom: "10px" }}>
                <strong>Date of Joining:</strong> {employee?.date_of_joining || "N/A"}
              </div>
            </div>
            <form className="flex-grow-1 d-flex flex-column mt-3">
              {/* Effective Date */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="col-form-label">Effective Date</label>
                  <DatePicker
                    selected={effectiveDate}
                    onChange={(date) => setEffectiveDate(date)}
                    dateFormat="yyyy-MM-dd"
                    className="form-control"
                  />
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-6">
                  <h4 className="text-primary">Earnings</h4>
                  {earnings.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <div className="d-flex mb-2 align-items-center">
                        <Select
                          options={filterOptions("earnings")}
                          value={item.salaryHead}
                          onChange={(option) => handleHeadSelection(option, index, "earning")}
                          placeholder="Select Salary Head"
                          className="select me-2 flex-grow-1"
                          menuPlacement="auto"
                          menuPosition="fixed"
                          styles={{
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            control: (provided) => ({ ...provided, overflow: "visible" }),
                          }}
                        />
                        <div className="input-group" style={{ width: "150px" }}>
                          <span className="input-group-text">₹</span>
                          <input
                            ref={(el) => { if (index === earnings.length - 1) amountRef.current = el; }}
                            className="form-control"
                            type="text"
                            placeholder="Amount"
                            value={item.amount}
                            onChange={(e) => handleAmountChange(e, index, "earning")}
                            onKeyDown={(e) => handleAmountKeyDown(e, index, "earning")}
                          />
                        </div>
                        {earnings.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link ms-2 text-danger"
                            onClick={() => removeEarning(item.id)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        )}
                      </div>
                      <hr className="my-2" style={{ borderTop: "1px solid #dee2e6" }} />
                    </React.Fragment>
                  ))}
                  <button type="button" className="btn btn-link" onClick={addEarning}>
                    <i className="fa-solid fa-plus-circle" /> Add More
                  </button>
                </div>
                <div className="col-sm-6">
                  <h4 className="text-primary">Deductions</h4>
                  {deductions.map((item, index) => (
                    <React.Fragment key={item.id}>
                      <div className="d-flex mb-2 align-items-center">
                        <Select
                          options={filterOptions("deductions")}
                          value={item.salaryHead}
                          onChange={(option) => handleHeadSelection(option, index, "deduction")}
                          placeholder="Select Salary Head"
                          className="select me-2 flex-grow-1"
                          menuPlacement="auto"
                          menuPosition="fixed"
                          styles={{
                            menu: (provided) => ({ ...provided, zIndex: 9999 }),
                            control: (provided) => ({ ...provided, overflow: "visible" }),
                          }}
                        />
                        <div className="input-group" style={{ width: "150px" }}>
                          <span className="input-group-text">₹</span>
                          <input
                            ref={(el) => { if (index === deductions.length - 1) amountRef.current = el; }}
                            className="form-control"
                            type="text"
                            placeholder="Amount"
                            value={item.amount}
                            onChange={(e) => handleAmountChange(e, index, "deduction")}
                            onKeyDown={(e) => handleAmountKeyDown(e, index, "deduction")}
                          />
                        </div>
                        {deductions.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-link ms-2 text-danger"
                            onClick={() => removeDeduction(item.id)}
                          >
                            <FontAwesomeIcon icon={faTrashAlt} />
                          </button>
                        )}
                      </div>
                      <hr className="my-2" style={{ borderTop: "1px solid #dee2e6" }} />
                    </React.Fragment>
                  ))}
                  <button type="button" className="btn btn-link" onClick={addDeduction}>
                    <i className="fa-solid fa-plus-circle" /> Add More
                  </button>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <h6>Total Earnings: <span className="text-primary">₹{totalEarnings.toFixed(2)}</span></h6>
                  <h6>Net Salary: <span className="text-primary">₹{grossSalary.toFixed(2)}</span></h6>  {/* Renamed for clarity */}
                </div>
                <div className="col-md-6 text-end">
                  <h6>Total Deductions: <span className="text-primary">₹{totalDeductions.toFixed(2)}</span></h6>
                </div>
              </div>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" type="button" onClick={handleSave}>
              {employee?.salary_status === "Existing" ? "Confirm Revision" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSalaryModal;